import React, { useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import menuCategories from "../data/menuCategories.json";
import menuItemsData from "../data/menuItems.json";
import SEO from "../components/SEO";

/* ─── Helper: get category color ────────────────────────────────── */
const CATEGORY_COLORS = {
  "Grill & BBQ": { accent: "#e85d04", light: "#fff8f0" },
  "Biryani & Mandi": { accent: "#9b1c2e", light: "#fdf0f2" },
  Specials: { accent: "#7c2d12", light: "#fff7ed" },
  "Soups & Starters": { accent: "#0e7490", light: "#ecfeff" },
  "Veg & Breads": { accent: "#166534", light: "#f0fdf4" },
  "Gravy & Mains": { accent: "#7c3aed", light: "#f5f3ff" },
  Mutton: { accent: "#065f46", light: "#ecfdf5" },
  "South Indian": { accent: "#92400e", light: "#fff7ed" },
  Seafood: { accent: "#0369a1", light: "#eff6ff" },
  Chicken: { accent: "#dc2626", light: "#fef2f2" },
  Chinese: { accent: "#1e3a5f", light: "#eff6ff" },
};

const getColor = (cat) =>
  CATEGORY_COLORS[cat] || { accent: "#9b1c2e", light: "#fdf0f2" };

/* ─── Related Items: same category ──────────────────────────────── */
const getRelated = (current) =>
  menuCategories.filter(
    (m) => m.category === current.category && m.id !== current.id,
  );

/* ─── Tag Pill ──────────────────────────────────────────────────── */
const TAG_STYLES = {
  "Top Seller": { bg: "bg-zinc-900", text: "text-white" },
  "Today's Special": {
    bg: "bg-white",
    text: "text-zinc-900",
    border: "border border-zinc-900",
  },
  Seasonal: { bg: "bg-zinc-200", text: "text-zinc-800" },
};
const TagPill = ({ tag }) => {
  if (!tag) return null;
  const s = TAG_STYLES[tag] || { bg: "bg-zinc-100", text: "text-zinc-600" };
  return (
    <span
      className={`inline-block px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest ${s.bg} ${s.text} ${s.border || ""}`}
    >
      {tag}
    </span>
  );
};

/* ─── Dish Card ─────────────────────────────────────────────────── */
const DishCard = ({ dish }) => (
  <div className="group relative bg-white rounded-2xl overflow-hidden border border-zinc-100 hover:border-zinc-900 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
    {/* Image */}
    <div className="relative overflow-hidden" style={{ paddingBottom: "68%" }}>
      <img
        src={`${menuItemsData.imageBase}${dish.thumb}`}
        alt={dish.name}
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
        onError={(e) => {
          e.target.src =
            "https://images.unsplash.com/photo-1504674900247-0877df9cc836?q=80&w=400&auto=format&fit=crop";
        }}
      />
      {/* Gradient */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
      {/* Tag */}
      {dish.tag && (
        <div className="absolute top-2.5 left-2.5">
          <TagPill tag={dish.tag} />
        </div>
      )}
    </div>
    {/* Name */}
    <div className="px-4 py-3.5">
      <p className="text-[13px] font-bold text-zinc-900 leading-snug tracking-wide">
        {dish.name}
      </p>
    </div>
  </div>
);

/* ─── Related Menu Card ─────────────────────────────────────────── */
const RelatedCard = ({ item }) => {
  const navigate = useNavigate();
  const { accent } = getColor(item.category);

  return (
    <div
      onClick={() => navigate(`/menu/${item.slug}/${item.id}`)}
      className="group relative rounded-2xl overflow-hidden cursor-pointer shadow-md border border-gray-100 hover:shadow-2xl hover:-translate-y-2 transition-all duration-400 bg-white flex flex-col"
    >
      {/* Image */}
      <div
        className="relative overflow-hidden"
        style={{ paddingBottom: "65%" }}
      >
        <img
          src={item.image}
          alt={item.title}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
          loading="lazy"
          onError={(e) => {
            e.target.src =
              "https://images.unsplash.com/photo-1504674900247-0877df9cc836?q=80&w=600&auto=format&fit=crop";
          }}
        />
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent opacity-70 group-hover:opacity-90 transition-opacity duration-300" />

        {/* Category badge */}
        <div
          className="absolute top-3 left-3 px-3 py-1 rounded-full text-[11px] font-bold tracking-wider uppercase text-white shadow"
          style={{ background: accent }}
        >
          {item.category}
        </div>

        {/* Hover circle */}
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300">
          <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-md border border-white/40 flex items-center justify-center">
            <svg
              className="w-5 h-5 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M17 8l4 4m0 0l-4 4m4-4H3"
              />
            </svg>
          </div>
        </div>
      </div>

      {/* Card body */}
      <div className="p-5 flex-1 flex flex-col">
        <h3
          className="text-[15px] font-bold text-gray-900 leading-snug mb-2 group-hover:transition-colors duration-200"
          style={{ fontFamily: "'Inter', sans-serif" }}
        >
          {item.title}
        </h3>
        <p className="text-sm text-gray-500 leading-relaxed line-clamp-2 flex-1">
          {item.description}
        </p>

        <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
          <span
            className="text-xs font-bold tracking-widest uppercase flex items-center gap-1.5 transition-all duration-200"
            style={{ color: accent }}
          >
            Explore
            <svg
              className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform duration-200"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M17 8l4 4m0 0l-4 4m4-4H3"
              />
            </svg>
          </span>
          <span className="text-xs text-gray-300 font-sans">#{item.id}</span>
        </div>
      </div>
    </div>
  );
};

/* ─── Main Page ─────────────────────────────────────────────────── */
const MenuDetailPage = () => {
  const { slug, id } = useParams();
  const navigate = useNavigate();

  /* Find the current menu item */
  const item = useMemo(
    () =>
      menuCategories.find(
        (m) => m.slug === slug && String(m.id) === String(id),
      ),
    [slug, id],
  );

  /* If not found, show 404-ish */
  if (!item) {
    return (
      <div className="min-h-screen bg-[#f9f5f0] flex flex-col items-center justify-center text-center px-4">
        <div className="w-20 h-20 rounded-full bg-red-50 flex items-center justify-center mb-6">
          <svg
            className="w-10 h-10 text-red-300"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1.5}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-gray-800 mb-3">
          Menu Not Found
        </h2>
        <p className="text-gray-500 mb-6">
          The menu category you're looking for doesn't exist.
        </p>
        <Link
          to="/menu"
          className="px-8 py-3 bg-[#9b1c2e] text-white text-sm font-bold uppercase tracking-widest rounded-full hover:bg-[#7d1624] transition-colors"
        >
          Back to Menu
        </Link>
      </div>
    );
  }

  const { accent } = getColor(item.category);
  const related = getRelated(item);

  const menuItemSchema = {
    "@context": "https://schema.org",
    "@type": "MenuItem",
    name: item.title,
    description:
      item.description || `Delicious ${item.title} at Barbequeen Restaurant.`,
    image: item.image,
    offers: {
      "@type": "Offer",
      price: item.price ? String(item.price).replace(/[^0-9.]/g, "") : "199",
      priceCurrency: "INR",
      availability: "https://schema.org/InStock",
    },
  };

  return (
    <div className="min-h-screen bg-[#f8f4f0]">
      <SEO
        title={`${item.title} | Barbequeen Menu`}
        metaTitle={`${item.title} | Barbequeen Restaurant Menu`}
        description={
          item.description ||
          `Enjoy authentic ${item.title} from Barbequeen Restaurant. Double marinated grill, dum biryani, tandoori & Arabian mandi.`
        }
        keywords={`${item.title}, Barbequeen menu, ${item.category}, Barbequeen dishes, Salem restaurant menu`}
        robots="index, follow"
        canonical={`https://barbequeen.com/menu/${item.slug}/${item.id}`}
        og={{
          type: "website",
          url: `https://barbequeen.com/menu/${item.slug}/${item.id}`,
          title: `${item.title} | Barbequeen Menu`,
          description:
            item.description || `Order ${item.title} at Barbequeen Restaurant.`,
          image: item.image,
          siteName: "Barbequeen Restaurant",
          locale: "en_IN",
        }}
        schema={menuItemSchema}
      />
      {/* ── Hero Banner ───────────────────────────────────────────── */}
      <section
        className="relative overflow-hidden"
        style={{ minHeight: "480px" }}
      >
        {/* Background image */}
        <img
          src={item.image}
          alt={item.title}
          className="absolute inset-0 w-full h-full object-cover object-center scale-105"
          style={{ filter: "brightness(0.45)" }}
          onError={(e) => {
            e.target.src =
              "https://images.unsplash.com/photo-1544025162-d76694265947?q=80&w=1600&auto=format&fit=crop";
          }}
        />

        {/* Overlay gradient */}
        <div
          className="absolute inset-0"
          style={{
            background: `linear-gradient(135deg, rgba(0,0,0,0.85) 0%, ${accent}55 60%, rgba(0,0,0,0.70) 100%)`,
          }}
        />

        {/* Decorative circles */}
        <div
          className="absolute -right-32 -top-32 w-96 h-96 rounded-full opacity-20 pointer-events-none"
          style={{ background: accent }}
        />
        <div
          className="absolute -left-20 bottom-0 w-72 h-72 rounded-full opacity-10 pointer-events-none"
          style={{ background: accent }}
        />

        {/* Breadcrumb */}
        <div className="relative z-10 max-w-6xl mx-auto px-4 lg:px-8 pt-10">
          <nav className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-white/50">
            <Link to="/" className="hover:text-white transition-colors">
              Home
            </Link>
            <svg
              className="w-3 h-3"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 5l7 7-7 7"
              />
            </svg>
            <Link to="/menu" className="hover:text-white transition-colors">
              Menu
            </Link>
            <svg
              className="w-3 h-3"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 5l7 7-7 7"
              />
            </svg>
            <span className="text-white/80 truncate max-w-[200px]">
              {item.title}
            </span>
          </nav>
        </div>

        {/* Hero content */}
        <div className="relative z-10 max-w-6xl mx-auto px-4 lg:px-8 py-16 pb-24">
          {/* Category pill */}
          <div className="inline-flex items-center gap-2 mb-5">
            <span
              className="px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest text-white shadow-lg"
              style={{ background: accent }}
            >
              {item.category}
            </span>
            <span className="text-white/40 text-xs">#{item.id}</span>
          </div>

          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-black text-white uppercase leading-tight tracking-wide mb-5 max-w-3xl"
            style={{
              fontFamily: "'Inter', sans-serif",
              textShadow: "0 2px 20px rgba(0,0,0,0.4)",
            }}
          >
            {item.title}
          </h1>

          <p className="text-gray-300 text-base sm:text-lg max-w-2xl leading-relaxed mb-8">
            {item.description}
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-wrap items-center gap-3">
            <a
              href="tel:+919597228788"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-sm font-bold uppercase tracking-widest text-white shadow-xl transition-all duration-200 hover:scale-105"
              style={{ background: accent }}
            >
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.948V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                />
              </svg>
              Call to Order
            </a>
            <button
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-sm font-bold uppercase tracking-widest text-white bg-white/10 border border-white/25 hover:bg-white/20 backdrop-blur-sm transition-all duration-200"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2.5}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              Go Back
            </button>
          </div>
        </div>
      </section>

      {/* ── Main content: Dishes + Related ───────────────────────── */}
      <div className="max-w-6xl mx-auto px-4 lg:px-8 py-14">
        <div>
          {/* Dishes + Related — full width */}
          <div>
            {/* ── DISH ITEMS GRID ── */}
            {(() => {
              const catData = menuItemsData.categories.find(
                (c) => c.categoryId === item.id,
              );
              const dishes = catData?.items || [];
              if (dishes.length === 0) return null;
              return (
                <div className="mb-12">
                  {/* Section Header */}
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-1 h-8 rounded-full bg-zinc-900" />
                    <div>
                      <p className="text-[10px] font-black uppercase tracking-[0.3em] text-zinc-400 mb-0.5">
                        What's Inside
                      </p>
                      <h2 className="text-xl font-black text-zinc-900 uppercase tracking-wide">
                        Dishes in this Menu
                      </h2>
                    </div>
                    <span className="ml-auto text-[11px] font-bold text-zinc-400 bg-zinc-100 rounded-full px-3 py-1">
                      {dishes.length} items
                    </span>
                  </div>

                  {/* Dish Grid */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    {dishes.map((dish) => (
                      <DishCard key={dish.id} dish={dish} />
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* ── Related in same category ── */}
            {related.length > 0 && (
              <div className="mb-12">
                <div className="flex items-center gap-3 mb-6">
                  <div
                    className="w-1 h-8 rounded-full"
                    style={{ background: accent }}
                  />
                  <div>
                    <p
                      className="text-xs font-black uppercase tracking-widest mb-0.5"
                      style={{ color: accent }}
                    >
                      Same Category
                    </p>
                    <h2 className="text-xl font-black text-gray-900 uppercase tracking-wide">
                      Related {item.category} Menus
                    </h2>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {related.map((r) => (
                    <RelatedCard key={r.id} item={r} />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MenuDetailPage;
